import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  ApiError,
  createRequirementVersionLink,
  deleteRequirementGlobally,
  deleteRequirementVersionLink,
  getRequirementByProject,
  listApplicability,
  listCategories,
  listProjectMembers,
  listRequirementComments,
  listRequirementStatuses,
  listRequirementVersionLinkTypes,
  listRequirementVersionsByProject,
  listRequirements,
  getMyPermissions,
  getProjectReviewers,
  listUsersOptional,
  listVerificationStatuses,
  listVerifications,
  patchRequirementByProject,
} from '@/api/client';
import { useDashboard } from '@/context/DashboardContext';
import type {
  Applicability,
  Category,
  EffectivePermissions,
  ProjectMember,
  Requirement,
  RequirementCommentItem,
  RequirementDetailPayload,
  RequirementStatus,
  RequirementVersion,
  RequirementPatchBody,
  RequirementVersionLink,
  User,
  Verification,
  VerificationStatus,
} from '@/api/types';
import AttachmentsPanel from '@/components/AttachmentsPanel';
import RequirementVersionDiffDialog from '@/components/RequirementVersionDiffDialog';
import CommentBody from '@/components/CommentBody';
import RequirementCommentComposer, {
  commentsLockedForApproval,
} from '@/components/RequirementCommentComposer';
import { StatusBadge, statusTagColorSwatchStyle } from '@/components/StatusBadge';
import type { ProjectOutletContext } from '@/types/projectOutlet';
import { formatUserLabel } from '@/utils/userLabel';
import {
  confirmEditApprovedRequirement,
  consumeEditApprovedAck,
  hasApprovedEditPrompted,
  isApprovedRequirement,
  markApprovedEditPrompted,
} from '@/utils/confirmEditApprovedRequirement';
import StatementEditor from '@/components/StatementEditor';
import DraftStatus from '@/components/DraftStatus';
import { useDraftAutosave } from '@/hooks/useDraftAutosave';
import { parseUser } from '@/utils/parseUser';
import {
  clearDraft,
  formatDraftTime,
  readDraft,
  requirementDraftKey,
} from '@/utils/requirementDraft';

/** The form fields kept in a local draft (issue #255). */
type EditDraftValues = {
  title: string;
  description: string;
  justification: string;
  statusId: number;
  categoryId: number;
  applicabilityId: number;
  authorId: number;
  reviewerId: number;
};

/** A stored draft found when the page opened. */
type DraftOffer = {
  savedAt: string;
  values: EditDraftValues;
  /** The requirement was saved again after the draft was taken. */
  stale: boolean;
  applied: boolean;
};

const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Your changes are kept on this device: sign in again and reopen this requirement.';

function approvalLabel(state: string): string {
  return state.replace(/_/g, ' ').toUpperCase();
}

function formatRelativeTime(iso: string): string {
  try {
    const d = new Date(iso);
    const ms = Date.now() - d.getTime();
    if (Number.isNaN(d.getTime()) || ms < 0) return '';
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { dateStyle: 'short' });
  } catch {
    return '';
  }
}

export default function EditRequirementPage() {
  const { basePath, projectId: pid } = useOutletContext<ProjectOutletContext>();
  const { requirementId: requirementIdParam } = useParams();
  const rid = Number(requirementIdParam);
  const navigate = useNavigate();
  const { csrfToken, dashboard, refresh: refreshDashboard } = useDashboard();

  const [detail, setDetail] = useState<RequirementDetailPayload | null>(null);
  const [versions, setVersions] = useState<RequirementVersion[]>([]);
  const [statuses, setStatuses] = useState<RequirementStatus[]>([]);
  const [verifStatuses, setVerifStatuses] = useState<VerificationStatus[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [applicability, setApplicability] = useState<Applicability[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [projectReviewerIds, setProjectReviewerIds] = useState<number[]>([]);
  const [users, setUsers] = useState<User[] | null>(null);
  const [projectReqs, setProjectReqs] = useState<Requirement[]>([]);
  const [verifications, setVerifications] = useState<Verification[]>([]);
  const [linkTypes, setLinkTypes] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState<RequirementCommentItem[]>([]);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffInitialPair, setDiffInitialPair] = useState<{
    oldVersionId?: number;
    newVersionId?: number;
  } | null>(null);
  const [perms, setPerms] = useState<EffectivePermissions | null>(null);
  const [newParentId, setNewParentId] = useState<number | ''>('');
  const [newLinkType, setNewLinkType] = useState('');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [justification, setJustification] = useState('');
  const [statusId, setStatusId] = useState(0);
  const [categoryId, setCategoryId] = useState(0);
  const [applicabilityId, setApplicabilityId] = useState(0);
  const [authorId, setAuthorId] = useState(0);
  const [reviewerId, setReviewerId] = useState(0);

  const [baseline, setBaseline] = useState({
    title: '',
    description: '',
    justification: '',
    status_id: 0,
    category_id: 0,
    applicability_id: 0,
    author_id: 0,
    reviewer_id: 0,
  });

  /**
   * Reload the page data. `resetForm: false` keeps the form as typed (used
   * after parent-link changes, which do not touch the form fields).
   */
  const loadSeq = useRef(0);
  const load = useCallback(async ({ resetForm = true }: { resetForm?: boolean } = {}) => {
    if (!Number.isFinite(pid) || !Number.isFinite(rid)) return;
    // Only the latest load may fill the form: an overlapping older one (StrictMode
    // runs the effect twice) would otherwise overwrite a restored draft.
    const seq = ++loadSeq.current;
    setLoadError(null);
    try {
      const [
        d,
        v,
        st,
        vst,
        cat,
        app,
        mem,
        reqs,
        ver,
        u,
        cmts,
        lt,
        permRes,
        revPool,
      ] = await Promise.all([
        getRequirementByProject(pid, rid),
        listRequirementVersionsByProject(pid, rid),
        listRequirementStatuses(),
        listVerificationStatuses(),
        listCategories(),
        listApplicability(),
        listProjectMembers(pid),
        listRequirements(pid),
        listVerifications(),
        listUsersOptional(),
        listRequirementComments(rid),
        listRequirementVersionLinkTypes(pid),
        getMyPermissions(pid).catch(() => null),
        getProjectReviewers(pid).catch(() => ({ user_ids: [] as number[] })),
      ]);
      if (seq !== loadSeq.current) return;
      setProjectReviewerIds(revPool.user_ids);
      setPerms(permRes);
      setDetail(d);
      setComments(cmts);
      setVersions(v);
      setStatuses(st);
      setVerifStatuses(vst);
      setCategories(cat.filter((c) => c.project_id === pid));
      setApplicability(app.filter((a) => a.project_id === pid));
      setMembers(mem);
      setProjectReqs(reqs);
      setVerifications(ver.filter((x) => x.project_id === pid));
      setUsers(u);
      setLinkTypes(lt);
      setNewLinkType((t) => (t && lt.includes(t) ? t : lt[0] ?? ''));
      if (!resetForm) return;

      setTitle(d.title);
      setDescription(d.description);
      setJustification(d.justification ?? '');
      setStatusId(d.status_id);
      setCategoryId(d.category_id);
      setApplicabilityId(d.applicability_id);
      setAuthorId(d.author_id);
      setReviewerId(d.reviewer_id);
      setBaseline({
        title: d.title,
        description: d.description,
        justification: d.justification ?? '',
        status_id: d.status_id,
        category_id: d.category_id,
        applicability_id: d.applicability_id,
        author_id: d.author_id,
        reviewer_id: d.reviewer_id,
      });
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load requirement');
    }
  }, [pid, rid]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!detail) return;
    if (!isApprovedRequirement(detail.approval_state)) return;
    if (hasApprovedEditPrompted(rid)) return;
    markApprovedEditPrompted(rid);
    if (consumeEditApprovedAck(rid)) return;
    if (!confirmEditApprovedRequirement(detail.approval_state)) {
      navigate(`${basePath}/requirements/${rid}`, { replace: true });
    }
  }, [basePath, detail, navigate, rid]);

  const userLabel = useCallback(
    (id: number) => formatUserLabel(id, { users, members }),
    [users, members],
  );

  const authorOptionIds = useMemo(() => {
    const ids = new Set(members.map((m) => m.user_id));
    if (authorId > 0) ids.add(authorId);
    return [...ids].sort((a, b) => a - b);
  }, [members, authorId]);

  /** Reviewer field: only users in the project reviewer pool (plus current value if legacy / not in pool). */
  const reviewerOptionIds = useMemo(() => {
    const ids = new Set(projectReviewerIds);
    if (reviewerId > 0) ids.add(reviewerId);
    return [...ids].sort((a, b) => a - b);
  }, [projectReviewerIds, reviewerId]);

  const verById = useMemo(() => {
    const m = new Map<number, Verification>();
    for (const v of verifications) m.set(v.id, v);
    return m;
  }, [verifications]);

  const verifStatusById = useMemo(() => {
    const m = new Map<number, VerificationStatus>();
    for (const s of verifStatuses) m.set(s.id, s);
    return m;
  }, [verifStatuses]);

  const reqTitleById = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of projectReqs) m.set(r.id, r.title);
    return m;
  }, [projectReqs]);

  const reqById = useMemo(() => {
    const m = new Map<number, Requirement>();
    for (const r of projectReqs) m.set(r.id, r);
    return m;
  }, [projectReqs]);

  const versionIdToReqId = useMemo(() => {
    const m = new Map<number, number>();
    for (const r of projectReqs) {
      if (r.current_version_id != null) {
        m.set(r.current_version_id, r.id);
      }
    }
    return m;
  }, [projectReqs]);

  const versionsNewestFirst = useMemo(
    () =>
      [...versions].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [versions],
  );
  const latestVersionCreatedAt = versionsNewestFirst[0]?.created_at;
  const lastApprovedVersion = useMemo(
    () =>
      versionsNewestFirst.find(
        (version) =>
          version.approval_state.toLowerCase() === 'approved' &&
          version.id !== detail?.current_version_id,
      ) ?? null,
    [detail?.current_version_id, versionsNewestFirst],
  );

  const parentCandidates = useMemo(
    () =>
      projectReqs.filter(
        (r) => r.id !== rid && r.current_version_id != null && r.current_version_id > 0,
      ),
    [projectReqs, rid],
  );

  const resolveParentReq = useCallback(
    (link: RequirementVersionLink) => {
      const prid = versionIdToReqId.get(link.target_version_id);
      if (prid == null) return null;
      return reqById.get(prid) ?? null;
    },
    [versionIdToReqId, reqById],
  );

  const dirty = useMemo(() => {
    return (
      title !== baseline.title ||
      description !== baseline.description ||
      justification !== baseline.justification ||
      statusId !== baseline.status_id ||
      categoryId !== baseline.category_id ||
      applicabilityId !== baseline.applicability_id ||
      authorId !== baseline.author_id ||
      reviewerId !== baseline.reviewer_id
    );
  }, [
    title,
    description,
    justification,
    statusId,
    categoryId,
    applicabilityId,
    authorId,
    reviewerId,
    baseline,
  ]);

  function revert() {
    setTitle(baseline.title);
    setDescription(baseline.description);
    setJustification(baseline.justification);
    setStatusId(baseline.status_id);
    setCategoryId(baseline.category_id);
    setApplicabilityId(baseline.applicability_id);
    setAuthorId(baseline.author_id);
    setReviewerId(baseline.reviewer_id);
    setSaveError(null);
  }

  // ---- Local draft (issue #255) -------------------------------------------
  const me = useMemo(() => parseUser(dashboard?.user), [dashboard?.user]);
  const draftKey =
    me && Number.isFinite(pid) && Number.isFinite(rid) ? requirementDraftKey(me.id, pid, rid) : null;
  const [draftChecked, setDraftChecked] = useState(false);
  const [draftOffer, setDraftOffer] = useState<DraftOffer | null>(null);

  const draftValues = useMemo<EditDraftValues>(
    () => ({
      title,
      description,
      justification,
      statusId,
      categoryId,
      applicabilityId,
      authorId,
      reviewerId,
    }),
    [title, description, justification, statusId, categoryId, applicabilityId, authorId, reviewerId],
  );

  const autosave = useDraftAutosave({
    key: draftChecked ? draftKey : null,
    dirty,
    values: draftValues,
    baseVersionId: detail?.current_version_id ?? null,
    // A draft from an older version stays untouched until the user decides.
    paused: Boolean(draftOffer && !draftOffer.applied),
  });

  /** Put draft values into the form, keeping loaded values that are no longer valid. */
  const applyDraft = useCallback(
    (v: EditDraftValues) => {
      setTitle(v.title);
      setDescription(v.description);
      setJustification(v.justification);
      if (perms?.is_project_reviewer && statuses.some((s) => s.id === v.statusId)) {
        setStatusId(v.statusId);
      }
      if (categories.some((c) => c.id === v.categoryId)) setCategoryId(v.categoryId);
      if (applicability.some((a) => a.id === v.applicabilityId)) {
        setApplicabilityId(v.applicabilityId);
      }
      if (members.some((m) => m.user_id === v.authorId)) setAuthorId(v.authorId);
      if (members.some((m) => m.user_id === v.reviewerId)) setReviewerId(v.reviewerId);
    },
    [perms, statuses, categories, applicability, members],
  );

  // Once the requirement is loaded, look for a draft left by an earlier visit.
  useEffect(() => {
    if (draftChecked || !detail || !draftKey) return;
    setDraftChecked(true);
    const stored = readDraft<EditDraftValues>(draftKey);
    if (!stored) return;
    const v = stored.values;
    const unchanged =
      v.title === baseline.title &&
      v.description === baseline.description &&
      v.justification === baseline.justification &&
      v.statusId === baseline.status_id &&
      v.categoryId === baseline.category_id &&
      v.applicabilityId === baseline.applicability_id &&
      v.authorId === baseline.author_id &&
      v.reviewerId === baseline.reviewer_id;
    if (unchanged) {
      clearDraft(draftKey);
      return;
    }
    const stale = stored.baseVersionId !== detail.current_version_id;
    if (!stale) applyDraft(v);
    setDraftOffer({ savedAt: stored.savedAt, values: v, stale, applied: !stale });
  }, [draftChecked, detail, draftKey, baseline, applyDraft]);

  function restoreDraft() {
    if (!draftOffer) return;
    applyDraft(draftOffer.values);
    setDraftOffer({ ...draftOffer, applied: true });
  }

  function discardDraft() {
    if (draftOffer?.applied) revert();
    if (draftKey) clearDraft(draftKey);
    setDraftOffer(null);
  }

  function cancelEditing() {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    autosave.discard();
    navigate(`${basePath}/requirements`);
  }

  function formatTs(iso: string): string {
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString(undefined, {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch {
      return iso;
    }
  }

  async function deleteRequirement() {
    if (
      !window.confirm(
        'Delete this requirement permanently? Linked traceability and data may be affected. This cannot be undone.',
      )
    ) {
      return;
    }
    const token = csrfToken ?? '';
    if (!token) {
      setSaveError('Missing CSRF token; refresh the page.');
      return;
    }
    setDeleteBusy(true);
    setSaveError(null);
    try {
      await deleteRequirementGlobally(rid, token);
      autosave.discard();
      await refreshDashboard();
      navigate(`${basePath}/requirements`);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleteBusy(false);
    }
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    const token = csrfToken ?? '';
    if (!token) {
      setSaveError('Missing CSRF token; refresh the page.');
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      const patch: RequirementPatchBody = {};
      if (title.trim() !== baseline.title) patch.title = title.trim();
      if (description.trim() !== baseline.description) patch.description = description.trim();
      if (justification.trim() !== baseline.justification.trim()) {
        patch.justification = justification.trim();
      }
      if (statusId !== baseline.status_id) {
        if (!perms?.is_project_reviewer) {
          setSaveError('Only project reviewers can change requirement status.');
          setSaving(false);
          return;
        }
        patch.status_id = statusId;
      }
      if (categoryId !== baseline.category_id) patch.category_id = categoryId;
      if (applicabilityId !== baseline.applicability_id) patch.applicability_id = applicabilityId;
      if (authorId !== baseline.author_id) patch.author_id = authorId;
      if (reviewerId !== baseline.reviewer_id) patch.reviewer_id = reviewerId;
      await patchRequirementByProject(pid, rid, patch, token);
      // Saved as a version: the local draft has done its job.
      autosave.discard();
      setDraftOffer(null);
      await refreshDashboard();
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setSaveError(SESSION_EXPIRED_MESSAGE);
      else setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      autosave.resume();
      setSaving(false);
    }
  }

  async function removeParentLink(linkId: number) {
    const token = csrfToken ?? '';
    if (!token) {
      setSaveError('Missing CSRF token; refresh the page.');
      return;
    }
    setLinkBusy(true);
    setSaveError(null);
    try {
      await deleteRequirementVersionLink(pid, linkId, token);
      await load({ resetForm: false });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to remove parent link');
    } finally {
      setLinkBusy(false);
    }
  }

  async function addParentLink() {
    const token = csrfToken ?? '';
    if (!token || newParentId === '' || !newLinkType.trim()) {
      setSaveError('Pick a parent requirement and link type.');
      return;
    }
    const srcVid = detail?.current_version_id;
    if (srcVid == null) {
      setSaveError('This requirement has no current version; cannot add parent links.');
      return;
    }
    const parentReq = reqById.get(Number(newParentId));
    const tgtVid = parentReq?.current_version_id;
    if (parentReq == null || tgtVid == null) {
      setSaveError('Parent requirement must have a current version.');
      return;
    }
    setLinkBusy(true);
    setSaveError(null);
    try {
      await createRequirementVersionLink(
        pid,
        {
          source_version_id: srcVid,
          target_version_id: tgtVid,
          link_type: newLinkType.trim(),
        },
        token,
      );
      setNewParentId('');
      await load({ resetForm: false });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to add parent link');
    } finally {
      setLinkBusy(false);
    }
  }

  const statusOptions = useMemo(() => {
    const forProject = statuses.filter((s) => s.project_id === pid);
    return forProject.length > 0 ? forProject : statuses;
  }, [statuses, pid]);

  const priorityLabel = useMemo(() => {
    const fields = detail?.custom_fields;
    if (!fields?.length) return '—';
    const p = fields.find((f) => f.label && /priority/i.test(f.label));
    return p?.value?.trim() || '—';
  }, [detail?.custom_fields]);

  const statusTitle = useMemo(() => {
    const s = statuses.find((x) => x.id === statusId);
    return s?.title ?? '—';
  }, [statuses, statusId]);

  const statusMeta = useMemo(() => {
    const s = statuses.find((x) => x.id === statusId);
    return s;
  }, [statuses, statusId]);

  const selectStitch =
    'w-full text-sm font-medium bg-stitch-elevated border border-stitch-border rounded-lg px-3 py-2 text-stitch-fg focus:border-stitch-accent focus:ring-1 focus:ring-stitch-accent/30 outline-hidden transition-colors';

  if (loadError) {
    return (
      <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-800 dark:text-red-100">
        {loadError}
        <div className="mt-3">
          <Link to={`${basePath}/requirements`} className="font-semibold text-stitch-accent underline">
            Back to requirements
          </Link>
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="text-stitch-muted text-sm py-12 text-center font-body bg-stitch-elevated -mx-6 md:-mx-8 px-6 rounded-lg">
        Loading requirement…
      </div>
    );
  }

  const { trace_summary: ts } = detail;
  const latestVersionLabel = versions.length > 0 ? `v${versions.length}` : '—';
  const canEditParents = detail.current_version_id != null && detail.current_version_id > 0;

  return (
    <div className="font-body text-stitch-fg -mx-6 md:-mx-8 -mt-6 md:-mt-8 px-6 md:px-8 pt-6 md:pt-8 min-h-full bg-stitch-canvas">
      <nav className="flex items-center gap-2 text-xs font-semibold text-stitch-muted mb-6 uppercase tracking-widest max-w-7xl mx-auto">
        <Link to={`${basePath}/requirements`} className="hover:text-stitch-accent transition-colors">
          Requirements
        </Link>
        <span className="material-symbols-outlined text-sm text-stitch-muted">chevron_right</span>
        <span className="text-stitch-accent font-bold font-headline">
          {detail.reference_code || `REQ-${detail.id}`}
        </span>
      </nav>

      {draftOffer ? (
        <div
          role="region"
          aria-label="Unsaved draft"
          className="max-w-7xl mx-auto mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-600/35 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-100"
        >
          <p>
            {draftOffer.stale && !draftOffer.applied
              ? `You have unsaved changes from ${formatDraftTime(draftOffer.savedAt)}, but this requirement has been saved again since. Restore them only if they still apply.`
              : `Recovered unsaved changes from ${formatDraftTime(draftOffer.savedAt)}. They are not saved until you press Save.`}
          </p>
          <div className="flex items-center gap-2">
            {draftOffer.stale && !draftOffer.applied ? (
              <button
                type="button"
                onClick={restoreDraft}
                className="rounded-md border border-amber-700/40 px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-amber-500/15"
              >
                Restore draft
              </button>
            ) : null}
            <button
              type="button"
              onClick={discardDraft}
              className="rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-amber-500/15"
            >
              Discard draft
            </button>
          </div>
        </div>
      ) : null}

      <form onSubmit={onSave} className="max-w-7xl mx-auto">
        <div className="grid grid-cols-12 gap-8">
          {/* Main editor */}
          <div className="col-span-12 lg:col-span-8 space-y-8">
            <section className="bg-stitch-surface p-6 md:p-8 rounded-xl shadow-xs border border-stitch-border">
              <div className="flex flex-col gap-2 mb-6">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-mono text-xs font-bold text-stitch-muted bg-stitch-higher px-2 py-1 rounded-sm border border-stitch-border">
                    {detail.reference_code || `#${detail.id}`}
                  </span>
                  <div className="h-1 w-1 bg-stitch-border rounded-full" />
                  <span className="text-xs font-medium text-stitch-accent-dim bg-stitch-higher px-2 py-1 rounded-sm border border-stitch-border uppercase tracking-wide">
                    {approvalLabel(detail.approval_state)}
                  </span>
                  {lastApprovedVersion && detail.current_version_id != null ? (
                    <button
                      type="button"
                      onClick={() => {
                        setDiffInitialPair({
                          oldVersionId: lastApprovedVersion.id,
                          newVersionId: detail.current_version_id!,
                        });
                        setDiffOpen(true);
                      }}
                      className="text-[10px] font-bold uppercase tracking-wider text-stitch-accent hover:underline"
                    >
                      Compare with last approved
                    </button>
                  ) : null}
                  <Link
                    to={`${basePath}/requirements/new?from=${rid}`}
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-stitch-accent hover:underline"
                  >
                    <span className="material-symbols-outlined text-sm">content_copy</span>
                    Duplicate
                  </Link>
                </div>
                <input
                  className="text-3xl font-bold font-headline bg-transparent border-none focus:ring-0 w-full p-0 text-stitch-fg placeholder:text-stitch-muted"
                  placeholder="Enter requirement title…"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-4 bg-stitch-elevated rounded-lg border border-stitch-border">
                <div>
                  <span className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
                    Priority
                  </span>
                  <div className="flex items-center gap-2 text-sm font-semibold text-stitch-fg">
                    <span className="material-symbols-outlined text-stitch-accent-dim text-lg">
                      priority_high
                    </span>
                    {priorityLabel}
                  </div>
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
                    Status
                  </span>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full shrink-0 bg-stitch-muted"
                      style={statusTagColorSwatchStyle(statusMeta?.tag_color)}
                    />
                    <select
                      className={`flex-1 min-w-0 ${selectStitch} py-1.5 font-semibold text-sm border-0 bg-transparent pl-0 focus:ring-0 disabled:opacity-50`}
                      value={statusId}
                      disabled={!perms?.is_project_reviewer}
                      title={
                        perms?.is_project_reviewer
                          ? undefined
                          : 'Only designated project reviewers can change status'
                      }
                      onChange={(e) => setStatusId(Number(e.target.value))}
                    >
                      {statusOptions.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.title}
                        </option>
                      ))}
                      {!statusOptions.some((s) => s.id === statusId) && (
                        <option value={statusId}>Status #{statusId}</option>
                      )}
                    </select>
                  </div>
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
                    Version
                  </span>
                  <span className="text-sm font-mono font-medium text-stitch-fg">{latestVersionLabel}</span>
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
                    Author
                  </span>
                  <span className="text-sm font-semibold text-stitch-fg line-clamp-2">
                    {userLabel(authorId)}
                  </span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
                    Category
                  </label>
                  <select className={selectStitch} value={categoryId} onChange={(e) => setCategoryId(Number(e.target.value))}>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                    {!categories.some((c) => c.id === categoryId) && (
                      <option value={categoryId}>Category #{categoryId}</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
                    Applicability
                  </label>
                  <select
                    className={selectStitch}
                    value={applicabilityId}
                    onChange={(e) => setApplicabilityId(Number(e.target.value))}
                  >
                    {applicability.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.title}
                      </option>
                    ))}
                    {!applicability.some((a) => a.id === applicabilityId) && (
                      <option value={applicabilityId}>Applicability #{applicabilityId}</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
                    Author (account)
                  </label>
                  <select className={selectStitch} value={authorId} onChange={(e) => setAuthorId(Number(e.target.value))}>
                    {authorOptionIds.map((id) => (
                      <option key={id} value={id}>
                        {userLabel(id)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
                    Reviewer
                  </label>
                  {reviewerOptionIds.length === 0 ? (
                    <p className="text-xs text-stitch-muted py-2">
                      No project reviewers configured. Add them in{' '}
                      <Link to={`${basePath}/settings/members`} className="text-stitch-accent underline font-semibold">
                        Project settings
                      </Link>
                      .
                    </p>
                  ) : (
                    <select
                      className={selectStitch}
                      value={reviewerId}
                      onChange={(e) => setReviewerId(Number(e.target.value))}
                    >
                      {reviewerOptionIds.map((id) => (
                        <option key={`r-${id}`} value={id}>
                          {userLabel(id)}
                          {!projectReviewerIds.includes(id) ? ' (not in reviewer list)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <p className="mt-3 text-[10px] text-stitch-muted flex flex-wrap items-center gap-2">
                Displayed status:
                <StatusBadge title={statusTitle} tagColor={statusMeta?.tag_color} />
              </p>
            </section>

            <section className="bg-stitch-surface rounded-xl shadow-xs border border-stitch-border overflow-hidden">
              <div className="flex items-center justify-between px-6 py-3 border-b border-stitch-border bg-stitch-elevated">
                <h3 className="text-xs font-bold uppercase tracking-widest text-stitch-muted font-headline">
                  Requirement statement
                </h3>
              </div>
              <div className="p-6 md:p-8">
                <StatementEditor
                  id="requirement-statement"
                  ariaLabel="Requirement statement"
                  value={description}
                  onChange={setDescription}
                  required
                  placeholder="Describe the requirement…"
                  minHeightClass="min-h-[300px]"
                  textareaClassName="w-full text-sm leading-relaxed text-stitch-fg bg-transparent border-none focus:ring-0 rounded-md p-0 resize-y placeholder:text-stitch-muted"
                />
              </div>
            </section>

            <section className="bg-stitch-surface rounded-xl shadow-xs border border-stitch-border overflow-hidden">
              <div className="px-6 py-3 border-b border-stitch-border bg-stitch-elevated">
                <label
                  htmlFor="requirement-rationale"
                  className="text-xs font-bold uppercase tracking-widest text-stitch-muted font-headline"
                >
                  Rationale (optional)
                </label>
              </div>
              <div className="p-6 md:p-8">
                <textarea
                  id="requirement-rationale"
                  className="w-full min-h-[140px] text-sm leading-relaxed text-stitch-fg bg-transparent border-none focus:ring-0 rounded-md p-0 resize-y placeholder:text-stitch-muted"
                  placeholder="Why this requirement exists…"
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                />
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <aside className="col-span-12 lg:col-span-4 space-y-6">
            <div className="bg-stitch-surface rounded-xl shadow-xs border border-stitch-border p-6">
              <div className="flex items-center gap-2 mb-6">
                <span className="material-symbols-outlined text-stitch-accent text-xl">account_tree</span>
                <h3 className="text-sm font-bold font-headline text-stitch-accent">Traceability</h3>
              </div>
              <div className="space-y-6">
                <div>
                  <p className="text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-3">
                    Upstream links (parent)
                  </p>
                  <div className="space-y-2">
                    {ts.parent_links.length === 0 ? (
                      <p className="text-xs text-stitch-muted py-1">None</p>
                    ) : (
                      ts.parent_links.map((l) => {
                        const parentReq = resolveParentReq(l);
                        return (
                          <div
                            key={l.id}
                            className="flex items-center justify-between gap-2 p-3 bg-stitch-elevated rounded-lg border border-stitch-border group"
                          >
                            <div className="min-w-0 flex-1">
                              {parentReq ? (
                                <Link
                                  to={`${basePath}/requirements/${parentReq.id}/edit`}
                                  className="block hover:opacity-90 transition-opacity"
                                  title={parentReq.title?.trim() || undefined}
                                >
                                  <span className="font-mono text-xs font-bold text-stitch-accent">
                                    {(parentReq.reference_code ?? '').trim() || `#${parentReq.id}`}
                                  </span>
                                  <span className="text-[10px] text-stitch-muted">{l.link_type}</span>
                                </Link>
                              ) : (
                                <div>
                                  <span className="font-mono text-[10px] font-bold text-stitch-accent">
                                    Version #{l.target_version_id}
                                  </span>
                                  <span className="block text-[10px] text-stitch-muted">{l.link_type}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {parentReq ? (
                                <Link
                                  to={`${basePath}/requirements/${parentReq.id}/edit`}
                                  className="p-1 text-stitch-muted hover:text-stitch-accent transition-colors"
                                  title="Open parent"
                                >
                                  <span className="material-symbols-outlined text-lg">open_in_new</span>
                                </Link>
                              ) : null}
                              <button
                                type="button"
                                title="Remove link"
                                disabled={linkBusy || !(csrfToken ?? '').length}
                                onClick={() => void removeParentLink(l.id)}
                                className="p-1 text-stitch-muted hover:text-red-600 dark:text-red-400 transition-colors disabled:opacity-40"
                              >
                                <span className="material-symbols-outlined text-lg">link_off</span>
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {canEditParents ? (
                    <div className="mt-4 pt-4 border-t border-stitch-border space-y-2">
                      <p className="text-[10px] font-bold text-stitch-muted uppercase tracking-wider">Add parent</p>
                      <select
                        className={selectStitch}
                        value={newParentId === '' ? '' : String(newParentId)}
                        onChange={(e) => setNewParentId(e.target.value === '' ? '' : Number(e.target.value))}
                      >
                        <option value="">Select requirement…</option>
                        {parentCandidates.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.reference_code || `#${r.id}`} — {r.title.slice(0, 48)}
                            {r.title.length > 48 ? '…' : ''}
                          </option>
                        ))}
                      </select>
                      <select
                        className={selectStitch}
                        value={newLinkType}
                        onChange={(e) => setNewLinkType(e.target.value)}
                      >
                        {linkTypes.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={linkBusy || newParentId === '' || !(csrfToken ?? '').length}
                        onClick={() => void addParentLink()}
                        className="w-full text-xs font-bold uppercase tracking-wider bg-linear-to-br from-primary to-primary-container text-white py-2 rounded-lg shadow-xs hover:opacity-95 disabled:opacity-40 transition-opacity"
                      >
                        {linkBusy ? 'Updating…' : 'Add upstream link'}
                      </button>
                    </div>
                  ) : (
                    <p className="mt-3 text-[10px] text-stitch-muted">
                      Parent links need a current requirement version.
                    </p>
                  )}
                </div>

                {ts.child_ids.length > 0 ? (
                  <div>
                    <p className="text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-3">
                      Child requirements
                    </p>
                    <div className="space-y-2">
                      {ts.child_ids.map((cid) => (
                        <Link
                          key={cid}
                          to={`${basePath}/requirements/${cid}/edit`}
                          className="flex items-center justify-between p-3 bg-stitch-elevated rounded-lg hover:bg-stitch-higher transition-colors"
                        >
                          <div className="min-w-0">
                            <span className="font-mono text-[10px] font-bold text-stitch-muted">#{cid}</span>
                            <span className="block text-xs font-medium text-stitch-fg truncate">
                              {reqTitleById.get(cid) ?? '—'}
                            </span>
                          </div>
                          <span className="material-symbols-outlined text-stitch-muted text-sm">chevron_right</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div>
                  <p className="text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-3">
                    Downstream links (verification)
                  </p>
                  <div className="space-y-2">
                    {ts.linked_test_ids.length === 0 ? (
                      <p className="text-xs text-stitch-muted py-1">None</p>
                    ) : (
                      ts.linked_test_ids.map((vid) => {
                        const v = verById.get(vid);
                        const vst = v ? verifStatusById.get(v.status_id) : undefined;
                        const borderColor = vst?.tag_color || undefined;
                        return (
                          <Link
                            key={vid}
                            to={`${basePath}/verifications/${vid}/edit`}
                            className="flex items-center justify-between p-3 bg-stitch-elevated rounded-lg border-l-2 border-stitch-muted hover:bg-stitch-higher transition-colors gap-2"
                            style={borderColor ? { borderLeftColor: borderColor } : undefined}
                          >
                            <div className="min-w-0 flex-1">
                              <span
                                className="font-mono text-[10px] font-bold text-stitch-accent-dim block"
                                style={borderColor ? { color: borderColor } : undefined}
                              >
                                {v?.reference_code ?? `TEST-${vid}`}
                              </span>
                              <span className="text-xs font-medium text-stitch-fg block truncate">
                                {v?.name ?? '—'}
                              </span>
                            </div>
                            <div className="shrink-0">
                              {vst?.title ? (
                                <StatusBadge title={vst.title} tagColor={vst.tag_color} />
                              ) : (
                                <span className="text-[10px] font-bold uppercase text-stitch-muted">—</span>
                              )}
                            </div>
                          </Link>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-stitch-surface rounded-xl shadow-xs border border-stitch-border flex flex-col max-h-[400px]">
              <div className="px-6 py-4 border-b border-stitch-border flex items-center justify-between shrink-0">
                <h3 className="text-sm font-bold font-headline text-stitch-accent">Change log &amp; discussion</h3>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {versions.length > 0 ? (
                  <div className="flex gap-3 opacity-90">
                    <span className="material-symbols-outlined text-stitch-muted text-lg shrink-0">history</span>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-stitch-muted">Version history</span>
                        <span className="text-[10px] text-stitch-muted">{latestVersionLabel}</span>
                      </div>
                      <p className="text-xs text-stitch-muted leading-relaxed">
                        {versions.length} snapshot(s). Latest:{' '}
                        {latestVersionCreatedAt ? formatTs(latestVersionCreatedAt) : '—'}.
                      </p>
                      <button
                        type="button"
                        disabled={versions.length < 2}
                        onClick={() => {
                          setDiffInitialPair(null);
                          setDiffOpen(true);
                        }}
                        className="text-[10px] font-bold text-stitch-accent hover:underline mt-2 inline-block uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Compare versions →
                      </button>
                    </div>
                  </div>
                ) : null}
                {comments.length === 0 && versions.length === 0 ? (
                  <p className="text-xs text-stitch-muted">No activity yet.</p>
                ) : null}
                {[...comments]
                  .sort(
                    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
                  )
                  .map((c) => (
                    <div key={c.id} className="flex gap-3">
                      <div className="h-6 w-6 rounded-full bg-stitch-accent flex items-center justify-center text-[10px] text-stitch-on-accent font-bold shrink-0">
                        {c.author_name
                          ? c.author_name
                              .split(/\s+/)
                              .map((p) => p[0])
                              .join('')
                              .slice(0, 2)
                              .toUpperCase()
                          : '?'}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs font-bold text-stitch-fg">{c.author_name}</span>
                          <span className="text-[10px] text-stitch-muted">{formatRelativeTime(c.created_at) || formatTs(c.created_at)}</span>
                        </div>
                        <CommentBody body={c.body} members={members} className="text-xs text-stitch-muted leading-relaxed whitespace-pre-wrap" />
                      </div>
                    </div>
                  ))}
              </div>
              <div className="px-6 py-3 border-t border-stitch-border bg-stitch-elevated shrink-0">
                <RequirementCommentComposer
                  requirementId={rid}
                  versionId={versionsNewestFirst[0]?.id ?? detail.current_version_id}
                  csrfToken={csrfToken}
                  locked={commentsLockedForApproval(
                    versionsNewestFirst[0]?.approval_state ?? detail.approval_state,
                  )}
                  members={members}
                  onPosted={(comment) => setComments((prev) => [comment, ...prev])}
                />
              </div>
            </div>

            <AttachmentsPanel
              projectId={pid}
              entityType="requirement"
              entityId={rid}
              canEdit={Boolean(perms?.edit_requirements)}
              csrfToken={csrfToken ?? ''}
            />
          </aside>
        </div>

        {saveError && (
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 max-w-lg w-[calc(100%-2rem)] rounded-lg bg-red-500/15 border border-red-500/35 text-red-900 dark:text-red-100 text-sm px-4 py-2 shadow-lg z-60">
            {saveError}
          </div>
        )}

        <footer className="sticky bottom-0 z-30 bg-stitch-surface/95 backdrop-blur-md border-t border-stitch-border px-4 md:px-8 py-4 flex flex-wrap items-center justify-between gap-3 shadow-stitch">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex items-center gap-2 text-stitch-muted hover:text-red-600 dark:text-red-400 transition-colors text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm disabled:opacity-40"
              disabled={deleteBusy || !(csrfToken ?? '').length}
              onClick={() => void deleteRequirement()}
            >
              <span className="material-symbols-outlined text-lg">delete</span>
              Delete requirement
            </button>
            <button
              type="button"
              className="text-xs font-bold uppercase tracking-wider text-stitch-muted hover:text-stitch-fg px-3 py-2"
              onClick={cancelEditing}
            >
              Cancel
            </button>
          </div>
          <div className="flex items-center gap-3">
            <DraftStatus status={autosave.status} savedAt={autosave.savedAt} dirty={dirty} saving={saving} />
            <button
              type="button"
              disabled={!dirty}
              onClick={() => {
                revert();
                setDraftOffer(null);
              }}
              className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-stitch-muted hover:bg-stitch-elevated transition-colors rounded-lg disabled:opacity-40"
            >
              Revert changes
            </button>
            <button
              type="submit"
              disabled={saving || !dirty}
              className="bg-linear-to-br from-primary to-primary-container text-white px-8 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest shadow-lg shadow-stitch active:scale-[0.98] transition-all disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save requirement'}
            </button>
          </div>
        </footer>
      </form>
      <RequirementVersionDiffDialog
        open={diffOpen}
        onClose={() => setDiffOpen(false)}
        projectId={pid}
        requirementId={rid}
        versions={versions}
        initialPair={diffInitialPair}
      />
    </div>
  );
}
